import {
  ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { ChartConfig } from '../../../types';
import { CHART_STYLE, truncateLabel, formatAxisNumber, formatDataValue, analyzeDecimals } from './chartStyles';

interface AreaVolumeViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

/**
 * Area chart with volume sub-pane (Rule 1b: Price + Volume + Date).
 * Renders price as area and volume as bars on a secondary y-axis.
 */
export function AreaVolumeView({ data, config }: AreaVolumeViewProps) {
  const xKey = config.xAxis ?? '';
  const priceKey = typeof config.yAxis === 'string' ? config.yAxis : config.yAxis?.[0] ?? '';
  const volumeKey = config.sizeAxis ?? '';
  const { hasDecimals, allSmall } = analyzeDecimals(data, priceKey);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={CHART_STYLE.margin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
          angle={CHART_STYLE.xAxis.angle}
          textAnchor={CHART_STYLE.xAxis.textAnchor}
          height={CHART_STYLE.xAxis.height}
          interval={0}
          tickFormatter={(v: string) => truncateLabel(v)}
        />
        <YAxis
          yAxisId="price"
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
          width={CHART_STYLE.yAxis.width}
          tickFormatter={formatAxisNumber}
        />
        <YAxis
          yAxisId="volume"
          orientation="right"
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: '#9CA3AF' }}
          width={50}
          tickFormatter={formatAxisNumber}
        />
        <Tooltip
          contentStyle={CHART_STYLE.tooltip}
          formatter={(value, name) => [
            formatDataValue(Number(value ?? 0), hasDecimals, allSmall),
            name ?? '',
          ]}
          labelFormatter={(label, _payload) =>
            label != null ? String(label) : ''
          }
        />
        <Area
          yAxisId="price"
          type="monotone"
          dataKey={priceKey}
          fill={CHART_STYLE.colors[0]}
          fillOpacity={0.15}
          stroke={CHART_STYLE.colors[0]}
          strokeWidth={2}
        />
        {volumeKey && (
          <Bar
            yAxisId="volume"
            dataKey={volumeKey}
            fill={CHART_STYLE.colors[3]}
            fillOpacity={0.4}
            radius={[1, 1, 0, 0]}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
